import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  TrendingUp, User, Plus, Check, X, Eye, EyeOff, 
  Trash2, LogOut, ArrowLeft, UserCircle, Wallet
} from 'lucide-react';
import { toast } from 'sonner';

// Account Card Component - MT5 Style
function AccountCard({ account, isActive, onSwitch, onRemove, removing }) {
  const formatBalance = (balance) => {
    if (!balance && balance !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(balance);
  };

  // Use either account_id or id field (backend uses 'id')
  const accountId = account.account_id || account.id;

  return (
    <div 
      className={`relative p-4 rounded-lg border transition-all ${
        isActive 
          ? 'bg-blue-500/10 border-blue-500/50' 
          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
      }`}
      data-testid={`account-card-${accountId}`}
    >
      {/* Active Indicator */}
      {isActive && (
        <div className="absolute top-2 right-2">
          <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-500/20 px-2 py-1 rounded-full">
            <Check className="w-3 h-3" />
            Active
          </span>
        </div>
      )}

      {/* Account Info */}
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isActive ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-800 text-zinc-400'
        }`}>
          <UserCircle className="w-7 h-7" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium truncate">{account.email}</span>
          </div>
          <div className="text-zinc-400 text-sm font-mono mt-0.5">
            ID: {accountId}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <Wallet className="w-3 h-3 text-zinc-500" />
              <span className="text-sm text-zinc-300 font-mono">
                {formatBalance(account.account_type === 'demo' ? account.demo_balance : account.balance)}
              </span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ${
              account.account_type === 'live' 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-orange-500/20 text-orange-400'
            }`}>
              {account.account_type?.toUpperCase() || 'DEMO'}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-800">
        {!isActive ? (
          <Button 
            onClick={() => onSwitch(account)}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            data-testid={`switch-to-${accountId}`}
          >
            <Check className="w-4 h-4 mr-1" />
            Switch to Account
          </Button>
        ) : (
          <Button 
            disabled
            className="flex-1 bg-zinc-800 text-zinc-500"
            size="sm"
          >
            <Check className="w-4 h-4 mr-1" />
            Currently Active
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(accountId)}
          disabled={removing === accountId || isActive}
          className={`text-zinc-400 hover:text-red-400 hover:bg-red-500/10 ${isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid={`remove-${accountId}`}
        >
          {removing === accountId ? (
            <span className="spinner w-4 h-4" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

// Add Account Modal
function AddAccountModal({ isOpen, onClose, onAccountAdded }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email');
  const [formData, setFormData] = useState({
    email: '',
    account_id: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loginData = loginMethod === 'email' 
        ? { email: formData.email, password: formData.password }
        : { account_id: formData.account_id, password: formData.password };
      
      const res = await authAPI.login(loginData);
      const { access_token, user: userData } = res.data;
      
      // Return the new account data to parent
      onAccountAdded({
        token: access_token,
        user: userData,
      });
      
      toast.success(`Account ${userData.id} added successfully!`);
      onClose();
      setFormData({ email: '', account_id: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#0f0f11] border border-zinc-800 rounded-xl w-full max-w-md p-6 relative z-[51]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Add Account</h2>
          <button 
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-800"
            data-testid="modal-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Login Method Toggle */}
          <div className="flex rounded-lg bg-zinc-900/50 p-1">
            <button
              type="button"
              onClick={() => setLoginMethod('email')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                loginMethod === 'email'
                  ? 'bg-primary text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('account_id')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                loginMethod === 'account_id'
                  ? 'bg-primary text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Account ID
            </button>
          </div>

          {loginMethod === 'email' ? (
            <div className="space-y-2">
              <Label htmlFor="add-email" className="text-zinc-300">Email</Label>
              <Input
                id="add-email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-zinc-900/50 border-zinc-800 text-white placeholder-zinc-500 focus:border-primary"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="add-account-id" className="text-zinc-300">Account ID</Label>
              <Input
                id="add-account-id"
                type="text"
                placeholder="1000001"
                value={formData.account_id}
                onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                required
                className="bg-zinc-900/50 border-zinc-800 text-white placeholder-zinc-500 focus:border-primary font-mono"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="add-password" className="text-zinc-300">Password</Label>
            <div className="relative">
              <Input
                id="add-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-zinc-900/50 border-zinc-800 text-white placeholder-zinc-500 focus:border-primary pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="modal-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 glow-primary"
              disabled={loading}
              data-testid="modal-add-btn"
            >
              {loading ? <span className="spinner mr-2" /> : <Plus className="w-4 h-4 mr-1" />}
              Add Account
            </Button>
          </div>
        </form>

        <p className="text-xs text-zinc-500 text-center mt-4">
          You can add accounts using your Master or Investor password
        </p>
      </div>
    </div>
  );
}

export default function AccountManagerPage() {
  const navigate = useNavigate();
  const { user, savedAccounts, activeAccountId, switchAccount, addAccount, removeAccount, logout } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [removing, setRemoving] = useState(null);

  // Helper to get account ID
  const getAccountId = (account) => account.id || account.account_id;

  // Handle account switch
  const handleSwitch = async (account) => {
    const accountId = getAccountId(account);
    try {
      await switchAccount(accountId);
      toast.success(`Switched to account ${accountId}`);
    } catch (error) {
      toast.error(error.message || 'Failed to switch account');
    }
  };

  // Handle account removal
  const handleRemove = async (accountId) => {
    if (accountId === activeAccountId) {
      toast.error('Cannot remove the currently active account');
      return;
    }
    
    setRemoving(accountId);
    try {
      removeAccount(accountId);
      toast.success('Account removed');
    } catch (error) {
      toast.error('Failed to remove account');
    } finally {
      setRemoving(null);
    }
  };

  // Handle adding new account
  const handleAccountAdded = (newAccountData) => {
    addAccount(newAccountData.token, newAccountData.user);
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="account-manager-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#0f0f11]">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-800"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
          </div>
          
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
            data-testid="add-account-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Account
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Manage Accounts</h1>
          <p className="text-zinc-400 mt-1">
            Switch between your trading accounts without logging out
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <div className="text-zinc-400 text-sm">Total Accounts</div>
            <div className="text-2xl font-bold text-white mt-1">{savedAccounts?.length || 0}</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <div className="text-zinc-400 text-sm">Active Account</div>
            <div className="text-lg font-mono text-blue-400 mt-1">{activeAccountId || '-'}</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <div className="text-zinc-400 text-sm">Session Type</div>
            <div className="text-lg text-white mt-1 capitalize">{user?.session_type || 'Master'}</div>
          </div>
        </div>

        {/* Accounts List */}
        <div className="space-y-3">
          {savedAccounts && savedAccounts.length > 0 ? (
            savedAccounts.map((account) => (
              <AccountCard
                key={getAccountId(account)}
                account={account}
                isActive={getAccountId(account) === activeAccountId}
                onSwitch={handleSwitch}
                onRemove={handleRemove}
                removing={removing}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-zinc-900/30 border border-zinc-800 rounded-lg">
              <UserCircle className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Accounts Added</h3>
              <p className="text-zinc-400 mb-4">Add your trading accounts to manage them from one place</p>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Your First Account
              </Button>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h3 className="text-blue-400 font-medium mb-2">💡 Quick Tips</h3>
          <ul className="text-sm text-zinc-400 space-y-1">
            <li>• You can add unlimited accounts to manage all your trading activities</li>
            <li>• Use Master password for full trading access</li>
            <li>• Use Investor password for read-only monitoring</li>
            <li>• Your accounts are stored locally and persist across sessions</li>
          </ul>
        </div>

        {/* Logout All */}
        {savedAccounts && savedAccounts.length > 1 && (
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => {
                if (window.confirm('Are you sure you want to logout from all accounts?')) {
                  logout();
                  navigate('/login');
                }
              }}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout from All Accounts
            </Button>
          </div>
        )}
      </main>

      {/* Add Account Modal */}
      <AddAccountModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAccountAdded={handleAccountAdded}
      />
    </div>
  );
}
